import { useState, useEffect, useCallback, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/use-auth";
import { Plus, Truck, GripVertical, ChevronDown, ChevronUp, Loader2, MapPin, Trash2 } from "lucide-react";

interface ShippingZone {
  readonly id: string;
  readonly zoneName: string;
  readonly cities: string;
  readonly zipRanges: string;
  readonly costOverride: number | null;
}

interface ShippingTemplate {
  readonly id: string;
  readonly name: string;
  readonly type: string;
  readonly baseCost: number;
  readonly perKgCost: number | null;
  readonly freeAbove: number | null;
  readonly isActive: boolean;
  readonly isBuiltIn: boolean;
  readonly position: number;
  readonly zones: readonly ShippingZone[];
}

const TYPE_MAP: Record<string, { label: string; className: string }> = {
  flat:   { label: "קבוע",        className: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30" },
  weight: { label: "לפי משקל",   className: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30" },
  price:  { label: "לפי מחיר",   className: "bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30" },
  free:   { label: "חינם",        className: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30" },
};

function formatNIS(agorot: number): string {
  return `₪${(agorot / 100).toFixed(2)}`;
}

function FormField({ label, children }: { readonly label: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-muted-foreground font-medium">{label}</label>
      {children}
    </div>
  );
}

export function ShippingSettingsPage(): ReactNode {
  const { accessToken } = useAuth();
  const [templates, setTemplates] = useState<readonly ShippingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("flat");
  const [newCost, setNewCost] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/shipping/templates/all", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const json = (await res.json()) as { success: boolean; data: readonly ShippingTemplate[] };
      if (json.success) setTemplates(json.data);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => { void fetchTemplates(); }, [fetchTemplates]);

  const toggleActive = useCallback(async (id: string, currentActive: boolean) => {
    await fetch(`/api/shipping/templates/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ isActive: !currentActive }),
    });
    void fetchTemplates();
  }, [accessToken, fetchTemplates]);

  const updateCost = useCallback(async (id: string, costNIS: string) => {
    const agorot = Math.round(Number(costNIS) * 100);
    if (isNaN(agorot) || agorot < 0) return;
    await fetch(`/api/shipping/templates/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ baseCost: agorot }),
    });
    void fetchTemplates();
  }, [accessToken, fetchTemplates]);

  const updateFreeAbove = useCallback(async (id: string, valueNIS: string) => {
    const agorot = valueNIS ? Math.round(Number(valueNIS) * 100) : null;
    await fetch(`/api/shipping/templates/${id}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ freeAbove: agorot }),
    });
    void fetchTemplates();
  }, [accessToken, fetchTemplates]);

  const deleteTemplate = useCallback(async (id: string) => {
    await fetch(`/api/shipping/templates/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    void fetchTemplates();
  }, [accessToken, fetchTemplates]);

  const addTemplate = useCallback(async () => {
    if (!newName.trim() || saving) return;
    setSaving(true);
    try {
      const res = await fetch("/api/shipping/templates", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newName,
          type: newType,
          baseCost: Math.round(Number(newCost) * 100) || 0,
        }),
      });
      const json = (await res.json()) as { success: boolean };
      if (json.success) {
        setShowAdd(false);
        setNewName("");
        setNewType("flat");
        setNewCost("");
        void fetchTemplates();
      }
    } finally {
      setSaving(false);
    }
  }, [newName, newType, newCost, saving, accessToken, fetchTemplates]);

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-500/10 flex items-center justify-center">
            <Truck className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">משלוחים</h1>
            <p className="text-sm text-muted-foreground">ניהול שיטות ואזורי משלוח</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} className="min-h-[38px]">
          <Plus className="h-4 w-4 me-1.5" />
          תבנית חדשה
        </Button>
      </div>

      {/* Add new template */}
      {showAdd && (
        <Card className="shadow-sm border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">תבנית משלוח חדשה</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              placeholder="שם תבנית"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="min-h-[42px]"
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {(["flat", "weight", "price", "free"] as const).map((t) => {
                const tm = TYPE_MAP[t]!;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setNewType(t)}
                    className={cn(
                      "p-2.5 rounded-lg border text-center text-sm font-medium transition-all min-h-[44px]",
                      newType === t
                        ? "border-primary bg-primary/10 text-primary"
                        : "hover:bg-muted/40 hover:border-muted-foreground/30"
                    )}
                  >
                    {tm.label}
                  </button>
                );
              })}
            </div>
            {newType !== "free" && (
              <FormField label="עלות בסיס (₪)">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newCost}
                  onChange={(e) => setNewCost(e.target.value)}
                  className="min-h-[42px]"
                  placeholder="0.00"
                />
              </FormField>
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={saving || !newName.trim()}
                onClick={() => void addTemplate()}
                className="min-h-[44px]"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : null}
                הוסף
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowAdd(false)} className="min-h-[44px]">
                ביטול
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
              <Truck className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">אין תבניות משלוח</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => {
            const tm = TYPE_MAP[t.type] ?? TYPE_MAP["flat"]!;
            const isExpanded = expandedId === t.id;
            return (
              <Card
                key={t.id}
                className={cn(
                  "shadow-sm transition-opacity",
                  !t.isActive && "opacity-50"
                )}
              >
                <CardContent className="p-0">
                  {/* Template header row */}
                  <div className="flex items-center gap-3 p-4">
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0 cursor-grab" />

                    {/* Type badge + info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{t.name}</span>
                        <span className={cn(
                          "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold",
                          tm.className
                        )}>
                          {tm.label}
                        </span>
                        {t.isBuiltIn && (
                          <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                            מובנה
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {t.type === "free" ? "חינם תמיד" : formatNIS(t.baseCost)}
                        {t.freeAbove !== null && ` · חינם מעל ${formatNIS(t.freeAbove)}`}
                      </p>
                    </div>

                    {/* Controls */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={t.isActive}
                        className={cn(
                          "relative h-6 w-11 rounded-full transition-colors focus:outline-none",
                          t.isActive ? "bg-primary" : "bg-muted"
                        )}
                        onClick={() => void toggleActive(t.id, t.isActive)}
                      >
                        <span className={cn(
                          "block h-5 w-5 rounded-full bg-white shadow-sm transition-transform",
                          t.isActive ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0.5 rtl:-translate-x-0.5"
                        )} />
                      </button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="min-h-[40px] min-w-[40px] p-0"
                        onClick={() => setExpandedId(isExpanded ? null : t.id)}
                        aria-label={isExpanded ? "סגור" : "ערוך"}
                      >
                        {isExpanded
                          ? <ChevronUp className="h-4 w-4" />
                          : <ChevronDown className="h-4 w-4" />
                        }
                      </Button>
                    </div>
                  </div>

                  {/* Expanded edit panel */}
                  {isExpanded && (
                    <div className="border-t px-4 pb-4 pt-4 space-y-4">
                      {t.type !== "free" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField label="עלות בסיס (₪)">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              defaultValue={(t.baseCost / 100).toFixed(2)}
                              onBlur={(e) => void updateCost(t.id, e.target.value)}
                              className="min-h-[42px]"
                            />
                          </FormField>
                          <FormField label="משלוח חינם מעל (₪)">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              defaultValue={t.freeAbove !== null ? (t.freeAbove / 100).toFixed(2) : ""}
                              onBlur={(e) => void updateFreeAbove(t.id, e.target.value)}
                              placeholder="ללא הגבלה"
                              className="min-h-[42px]"
                            />
                          </FormField>
                        </div>
                      )}

                      {/* Zones */}
                      {t.zones.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            אזורים ({t.zones.length})
                          </p>
                          <div className="space-y-1.5">
                            {t.zones.map((z) => (
                              <div key={z.id} className="flex items-center gap-3 text-sm bg-muted/40 rounded-lg p-3">
                                <span className="font-medium">{z.zoneName}</span>
                                <span className="text-muted-foreground flex-1 truncate text-xs">{z.cities}</span>
                                {z.costOverride !== null && (
                                  <span className="font-mono font-semibold text-xs">{formatNIS(z.costOverride)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {!t.isBuiltIn && (
                        <Button
                          size="sm"
                          variant="destructive"
                          className="min-h-[44px] gap-1.5"
                          onClick={() => void deleteTemplate(t.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          מחק תבנית
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
